import { createI18n } from 'vue-i18n'

// 默认语言（根据localStorage或环境变量决定）
const getDefaultLocale = () => {
  return localStorage.getItem('lang') || import.meta.env.VITE_LANG || 'en'
}

// 创建 i18n 实例
const i18n = createI18n({
  legacy: false, // 使用 Composition API 模式
  locale: getDefaultLocale(),
  globalInjection: true,
  messages: {}, // 初始为空，稍后动态加载
  fallbackLocale: 'en', // 回退语言
  missingWarn: false, // 关闭缺失警告（可选）
  fallbackWarn: false, // 关闭回退警告（可选）
})

// 已加载的语言包缓存
const loadedLanguages = new Set()

// 使用 import.meta.glob 预加载所有语言包的引用（不会立即加载内容）
// 这是 Vite 推荐的动态导入方式
const localeModules = import.meta.glob('./locales/*.json')

// 语言代码映射（处理不同的命名格式）
// 关键：将代码中使用的语言代码映射到实际的文件名
const langMap = {
  'en': 'en-gb',           // ✅ en → en-gb.json
  'en-gb': 'en-gb',        // ✅ en-gb → en-gb.json
  'zh-CN': 'zh-cn',        // ✅ zh-CN → zh-cn.json
  'zh-cn': 'zh-cn',
  'zh-TW': 'zh-tw',        // ✅ zh-TW → zh-tw.json
  'zh-tw': 'zh-tw',
  'de': 'de',
  'fr': 'fr',
  'pl': 'pl',
  'jp': 'jp',
  'ru': 'ru',
  'kr': 'kr',
  'vn': 'vn',
  'tr': 'tr',
  'br': 'br',
  'idn': 'idn',
  'id': 'idn',        // ISO 639-1：印尼语
  'in': 'idn',        // 印尼语旧 ISO 639-1 码（站内约定也用 in）
  'th': 'th',
  'pt': 'pt',
  'es': 'es',
}

/**
 * 动态加载语言包
 * @param {string} lang - 语言代码
 * @returns {Promise<void>}
 */
export async function loadLanguageAsync(lang) {
  // 如果语言包已加载，直接返回
  if (loadedLanguages.has(lang)) {
    if (i18n.global.locale.value !== lang) {
      i18n.global.locale.value = lang
    }
    return Promise.resolve()
  }

  // 标准化语言代码 - 映射到实际文件名
  const normalizedLang = langMap[lang] || lang.toLowerCase()

  // 构建文件路径
  const modulePath = `./locales/${normalizedLang}.json`

  console.log(`🌐 开始加载语言包: ${lang} → ${normalizedLang} (文件: ${modulePath})`)

  try {
    // 检查模块是否存在
    if (!localeModules[modulePath]) {
      console.error(`❌ 语言包文件不存在: ${modulePath}`)
      console.log('📂 可用的语言包文件:', Object.keys(localeModules))

      // 尝试使用英语作为回退
      if (lang !== 'en' && lang !== 'en-gb') {
        console.log('🔄 尝试加载英语作为回退语言')
        return loadLanguageAsync('en')
      }

      throw new Error(`语言包 ${lang} (${modulePath}) 不存在`)
    }

    // 动态导入语言包
    const messages = await localeModules[modulePath]()

    // 设置语言包
    i18n.global.setLocaleMessage(lang, messages.default || messages)

    // 标记为已加载
    loadedLanguages.add(lang)

    // 切换当前语言
    i18n.global.locale.value = lang

    console.log(`✅ 语言包 ${lang} 加载成功`)

    return Promise.resolve()
  } catch (error) {
    console.error(`❌ 加载语言包 ${lang} 失败:`, error)

    // 如果加载失败，回退到英语（只尝试一次，避免无限循环）
    if (lang !== 'en' && lang !== 'en-gb' && !loadedLanguages.has('en')) {
      console.log('🔄 尝试加载英语作为回退语言')
      return loadLanguageAsync('en')
    }

    return Promise.reject(error)
  }
}

/**
 * 检测浏览器语言，映射到项目支持的语言key
 * 优先遍历 navigator.languages（用户语言偏好列表，按优先级排序）
 * @returns {string|null} 匹配到的语言key，无匹配返回null（由调用方走后端接口兜底）
 */
export function detectBrowserLang() {
  const langs = navigator.languages || (navigator.language ? [navigator.language] : [])
  console.log('🔍 [detectBrowserLang] navigator.languages:', JSON.stringify(langs))
  console.log('🔍 [detectBrowserLang] navigator.language:', navigator.language)
  if (!langs.length) {
    console.log('🔍 [detectBrowserLang] 无浏览器语言信息，返回null')
    return null
  }

  // 主语言代码 → 项目语言key（直接对应的）
  const primaryMap = {
    'en': 'en',
    'de': 'de',
    'fr': 'fr',
    'pl': 'pl',
    'es': 'es',
    'ru': 'ru',
    'tr': 'tr',
    'th': 'th',
  }

  for (const full of langs) {
    const lower = String(full).toLowerCase()
    const primary = lower.split('-')[0]
    console.log(`🔍 [detectBrowserLang] 检查: "${full}" → primary="${primary}"`)

    // 中文：区分简繁（zh-TW/zh-HK/zh-Hant → 繁体，其余 → 简体）
    if (primary === 'zh') {
      const result = (lower.includes('tw') || lower.includes('hk') || lower.includes('hant')) ? 'zh-TW' : 'zh-CN'
      console.log(`🔍 [detectBrowserLang] 中文命中 → ${result}`)
      return result
    }

    // 特殊映射：浏览器语言代码 ≠ 项目语言key
    if (primary === 'ja') { console.log('🔍 [detectBrowserLang] ja → jp'); return 'jp' }
    if (primary === 'ko') { console.log('🔍 [detectBrowserLang] ko → kr'); return 'kr' }
    if (primary === 'vi') { console.log('🔍 [detectBrowserLang] vi → vn'); return 'vn' }
    if (primary === 'id' || primary === 'in') { console.log('🔍 [detectBrowserLang] id/in → idn'); return 'idn' }
    if (primary === 'pt') {
      const r = lower.includes('br') ? 'br' : 'pt'
      console.log(`🔍 [detectBrowserLang] pt → ${r}`)
      return r
    }

    // 直接对应
    if (primaryMap[primary]) {
      console.log(`🔍 [detectBrowserLang] 直接映射: ${primary} → ${primaryMap[primary]}`)
      return primaryMap[primary]
    }
  }

  console.log('🔍 [detectBrowserLang] 所有语言均未匹配，返回null')
  return null
}

/**
 * 预加载常用语言包（可选）
 * 可以在应用空闲时预加载，提升切换体验
 */
export function preloadLanguages(langs = []) {
  langs.forEach(lang => {
    if (!loadedLanguages.has(lang)) {
      // 使用 requestIdleCallback 在浏览器空闲时加载
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          loadLanguageAsync(lang).catch(() => {})
        })
      } else {
        // 降级方案：使用 setTimeout
        setTimeout(() => {
          loadLanguageAsync(lang).catch(() => {})
        }, 2000)
      }
    }
  })
}

/**
 * 语言包加载完成的 Promise
 * 外部可通过 await i18nReady 等待语言包加载完成
 */
export let i18nReady = Promise.resolve()

// 初始化：加载默认语言
const defaultLocale = getDefaultLocale()
console.log(`🚀 初始化语言: ${defaultLocale}`)

// 捕获加载 Promise 并暴露给外部使用
i18nReady = loadLanguageAsync(defaultLocale).catch(error => {
  console.error('❌ 加载默认语言包失败:', error)
  if (defaultLocale !== 'en' && defaultLocale !== 'en-gb') {
    console.log('🔄 尝试加载英语作为后备')
    return loadLanguageAsync('en').catch(err => {
      console.error('❌ 加载英语语言包也失败:', err)
    })
  }
})

export default i18n